import CloseIcon from 'svg/CloseIcon';
import { Button, ButtonLink, ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';

interface AlertModalProps {
  title: string;
  description: string | React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  isOpen: boolean;
  handleClose: () => void;
  loading?: boolean;
  confirmBtnVariant: 'primary' | 'brand' | 'brand-secondary';
  confirmBtnText: string;
  /** Glyph rendered ahead of the confirm label. */
  confirmBtnIcon?: React.ReactNode;
  hideCancelBtn?: boolean;
  /** Secondary button label; defaults to "Cancel". */
  cancelBtnText?: string;
  /** Puts the secondary button above the confirm — Figma's "Go back" / "Cancel" order. */
  secondaryFirst?: boolean;
  /** Side-by-side buttons in a divided footer, rather than stacked full-width. */
  hasInlineButtons?: boolean;
  /** Replaces the default alert glyph — an illustration, or any other artwork. */
  icon?: React.ReactNode;
  /** Renders the confirm button as a link to this href instead of firing `onConfirm`. */
  confirmBtnHref?: string;
  /** Renders the cancel button as a link to this href instead of firing `onCancel`. */
  cancelBtnHref?: string;
  /** Figma's 640px desktop dialog: wider panel and a divided, side-by-side footer
   *  from lg up. Below lg the sheet is unchanged. */
  isWideDesktop?: boolean;
}

const AlertModal: React.FC<AlertModalProps> = ({
  title,
  description,
  onConfirm,
  onCancel,
  isOpen,
  handleClose,
  loading = false,
  confirmBtnVariant = 'primary',
  confirmBtnText = 'Confirm',
  confirmBtnIcon,
  hideCancelBtn = false,
  cancelBtnText = 'Cancel',
  secondaryFirst = false,
  hasInlineButtons = false,
  icon,
  confirmBtnHref,
  cancelBtnHref,
  isWideDesktop = false,
}) => {
  const buttonClassName = classNames(
    hasInlineButtons && 'flex-1',
    isWideDesktop && 'lg:min-w-0 lg:flex-1',
  );

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      classNameMaxWidth={isWideDesktop ? 'sm:max-w-md lg:max-w-[40rem]' : undefined}
    >
      <div className="flex justify-end px-ds-3xl py-5  max-sm:px-ds-xl">
        <ButtonText onClick={handleClose} aria-label="Close">
          <CloseIcon className="h-6 w-6" />
        </ButtonText>
      </div>
      <div className="flex flex-col items-center justify-center px-ds-3xl pb-ds-3xl max-sm:px-ds-xl max-sm:pb-ds-xl">
        {icon ?? <img src="/images/players/alert.png" alt="" className="h-auto w-20" />}
        <h2 className="typography-product-display mt-ds-lg text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {title}
        </h2>
        <p className="typography-product-body  mt-ds-xl max-w-[20rem] text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {description}
        </p>
        <div
          className={classNames(
            'mt-10 flex w-full gap-ds-lg',
            hasInlineButtons && 'border-t border-color-border-card-lightmode pt-ds-xl',
            !hasInlineButtons && (secondaryFirst ? 'flex-col-reverse' : 'flex-col'),
            hasInlineButtons && secondaryFirst && 'flex-row-reverse',
            // The rule runs edge to edge, so it has to escape the body's padding.
            isWideDesktop &&
              'lg:-mx-ds-3xl lg:gap-ds-xl lg:border-t lg:border-color-border-card-lightmode lg:px-ds-3xl lg:pt-ds-xl',
            isWideDesktop && (secondaryFirst ? 'lg:flex-row-reverse' : 'lg:flex-row'),
          )}
        >
          {confirmBtnHref ? (
            <ButtonLink
              href={confirmBtnHref}
              variant={confirmBtnVariant}
              size="lg"
              iconLeft={confirmBtnIcon}
              className={buttonClassName}
            >
              {confirmBtnText}
            </ButtonLink>
          ) : (
            <Button
              variant={confirmBtnVariant}
              loading={loading}
              size="lg"
              iconLeft={confirmBtnIcon}
              className={buttonClassName}
              onClick={onConfirm}
            >
              {confirmBtnText}
            </Button>
          )}
          {!hideCancelBtn &&
            (cancelBtnHref ? (
              <ButtonLink
                href={cancelBtnHref}
                variant="secondary"
                size="lg"
                className={buttonClassName}
              >
                {cancelBtnText}
              </ButtonLink>
            ) : (
              <Button
                variant="secondary"
                size="lg"
                disabled={loading}
                className={buttonClassName}
                onClick={onCancel}
              >
                {cancelBtnText}
              </Button>
            ))}
        </div>
      </div>
    </Modal>
  );
};

export default AlertModal;
